import { supabase } from '../../supabase';
import { resyncOnResubscribe } from '../../utils/realtime/resyncOnResubscribe';
import { subscribeAppResume } from '../../utils/ui/appResumeGate';

const BURST_MS = 500;

/**
 * KAN-200 — live "your company's access changed" signal. `company_access_pings`
 * (one data-free row per tenant, members can read) is in the realtime
 * publication: ANY event means "refetch `get_my_company_access`". One channel
 * per tenant, shared by every mounted reader (the gate sits on many buttons);
 * EVERY reader's callback runs on a ping — the first registrant's callback alone
 * left the suspended screen deaf to its own unlock (KAN-866). Bursts dedupe to
 * one call per BURST_MS with a trailing call.
 */
const live = new Map(); // tenantId -> { listeners: Set, channel, timer, lastRun }

export function startCompanyAccessLive(tenantId, onPing) {
  if (!tenantId) return () => {};
  let entry = live.get(tenantId);
  if (!entry) {
    entry = { listeners: new Set(), channel: null, timer: null, lastRun: 0, stopResume: null };
    const e = entry;
    const run = () => {
      e.lastRun = Date.now();
      e.listeners.forEach((fn) => { try { fn(); } catch (_err) { /* one reader never breaks the others */ } });
    };
    const ping = () => {
      const elapsed = Date.now() - e.lastRun;
      if (elapsed >= BURST_MS) { run(); return; }
      if (!e.timer) e.timer = setTimeout(() => { e.timer = null; run(); }, BURST_MS - elapsed);
    };
    e.channel = supabase
      .channel(`company-access-ping-${tenantId}`, { config: { private: true } })
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'company_access_pings', filter: `tenant_id=eq.${tenantId}`,
      }, (payload) => {
        if (payload?.eventType === 'DELETE') return;
        ping();
      })
      .subscribe(resyncOnResubscribe(run)); // dropped + back: one re-check (KAN-1044)
    // KAN-1044: the ONE backup read on resume (60s away) for every reader of this notice.
    e.stopResume = subscribeAppResume(run);
    live.set(tenantId, e);
  }
  // A fresh function per registration, so two readers passing the same callback still count twice.
  const listener = () => onPing();
  entry.listeners.add(listener);
  return () => {
    const cur = live.get(tenantId);
    if (!cur) return;
    cur.listeners.delete(listener);
    if (cur.listeners.size > 0) return;
    if (cur.timer) clearTimeout(cur.timer);
    if (cur.stopResume) cur.stopResume();
    supabase.removeChannel(cur.channel);
    live.delete(tenantId);
  };
}
